import {
  getGetSensorQueryKey,
  getGetSensorQueryOptions,
  useListSensors,
  type SensorsSensor,
} from '@smarthome/api';
import { useSensorStream } from '@smarthome/ui';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  GripVertical,
  Pencil,
  Search,
  X,
} from 'lucide-react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

const MAX_SENSORS = 10;
const MIN_SENSOR_WIDTH_PX = 120;

/**
 * Sensor entry with optional display name override
 */
const sensorEntrySchema = z.object({
  sensorHumanId: z.string().min(1),
  displayName: z.string().optional(),
});

type SensorEntry = z.infer<typeof sensorEntrySchema>;

/**
 * Legacy config schema (single sensor - oldest format)
 */
const legacySingleSensorConfigSchema = z.object({
  sensorHumanId: z.string(),
  showUnit: z.boolean().optional(),
  showLastUpdated: z.boolean().optional(),
});

/**
 * Legacy config schema (multiple sensors as string array)
 */
const legacyMultipleSensorsConfigSchema = z.object({
  sensorHumanIds: z.array(z.string()),
  showUnit: z.boolean().optional(),
  showLastUpdated: z.boolean().optional(),
});

/**
 * New config schema (multiple sensors with display name support)
 */
const newQueryValueConfigSchema = z.object({
  sensors: z
    .array(sensorEntrySchema)
    .min(1, 'At least one sensor is required')
    .max(MAX_SENSORS, `Maximum ${MAX_SENSORS} sensors allowed`),
  showUnit: z.boolean().optional(),
  showLastUpdated: z.boolean().optional(),
  showLabels: z.boolean().optional(),
});

/**
 * Query Value widget config schema with backward compatibility
 */
export const queryValueConfigSchema = z.union([
  newQueryValueConfigSchema,
  legacyMultipleSensorsConfigSchema.transform((legacy) => ({
    sensors: legacy.sensorHumanIds.map((id) => ({ sensorHumanId: id })),
    showUnit: legacy.showUnit,
    showLastUpdated: legacy.showLastUpdated,
    showLabels: true,
  })),
  legacySingleSensorConfigSchema.transform((legacy) => ({
    sensors: legacy.sensorHumanId ? [{ sensorHumanId: legacy.sensorHumanId }] : [],
    showUnit: legacy.showUnit,
    showLastUpdated: legacy.showLastUpdated,
    showLabels: true,
  })),
]);

export type QueryValueConfig = z.infer<typeof newQueryValueConfigSchema>;

/**
 * Normalize config to handle all legacy formats
 */
function normalizeConfig(config: unknown): SensorEntry[] {
  const c = config as Record<string, unknown>;

  // New format: sensors array with objects
  if (Array.isArray(c['sensors']) && c['sensors'].length > 0) {
    return c['sensors'] as SensorEntry[];
  }

  // Legacy format: sensorHumanIds string array
  if (Array.isArray(c['sensorHumanIds']) && c['sensorHumanIds'].length > 0) {
    return (c['sensorHumanIds'] as string[]).map((id) => ({ sensorHumanId: id }));
  }

  // Oldest format: single sensorHumanId string
  if (typeof c['sensorHumanId'] === 'string' && c['sensorHumanId']) {
    return [{ sensorHumanId: c['sensorHumanId'] }];
  }

  return [];
}

/**
 * Reorderable multi-select component for sensors with display name support
 */
function SensorReorderableMultiSelect({
  sensors,
  onChange,
  error,
}: {
  sensors: SensorEntry[];
  onChange: (sensors: SensorEntry[]) => void;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: sensorsData, isLoading } = useListSensors();
  const allSensors = sensorsData?.sensors ?? [];

  const selectedIds = sensors.map((s) => s.sensorHumanId);
  const availableSensors = allSensors.filter((s) => s.humanId && !selectedIds.includes(s.humanId));

  // Filter sensors by search query
  const filteredSensors = useMemo(() => {
    if (!searchQuery.trim()) return availableSensors;
    const query = searchQuery.toLowerCase();
    return availableSensors.filter(
      (s) => s.name?.toLowerCase().includes(query) || s.humanId?.toLowerCase().includes(query)
    );
  }, [availableSensors, searchQuery]);

  const handleOpenDropdown = () => {
    setIsOpen(true);
    setSearchQuery('');
    // Focus search input after dropdown opens
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleCloseDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const getSensorInfo = (id: string) => allSensors.find((s) => s.humanId === id);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    setTimeout(() => {
      const target = e.target as HTMLElement;
      target.style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      const sourceIndex = draggedIndex;
      if (sourceIndex === null || sourceIndex === targetIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragCounter.current = 0;
        return;
      }

      const newOrder = [...sensors];
      const [removed] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, removed!);
      onChange(newOrder);

      setDraggedIndex(null);
      setDragOverIndex(null);
      dragCounter.current = 0;
    },
    [draggedIndex, sensors, onChange]
  );

  const handleAdd = (sensor: SensorsSensor) => {
    if (sensors.length >= MAX_SENSORS || !sensor.humanId) return;
    onChange([...sensors, { sensorHumanId: sensor.humanId }]);
    handleCloseDropdown();
  };

  const handleRemove = (index: number) => {
    onChange(sensors.filter((_, i) => i !== index));
  };

  const handleStartEdit = (index: number) => {
    const sensor = sensors[index];
    setEditValue(sensor?.displayName ?? '');
    setEditingIndex(index);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const newSensors = [...sensors];
    newSensors[editingIndex] = {
      ...newSensors[editingIndex]!,
      displayName: editValue.trim() || undefined,
    };
    onChange(newSensors);
    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Sensors
        <span className="ml-2 text-xs font-normal text-gray-400">
          ({sensors.length}/{MAX_SENSORS})
        </span>
      </label>

      {/* Sensor list with reordering and edit */}
      {sensors.length > 0 && (
        <div className="mb-2 space-y-1.5">
          {sensors.map((sensor, index) => {
            const sensorInfo = getSensorInfo(sensor.sensorHumanId);
            const isEditing = editingIndex === index;
            const originalName = sensorInfo?.name || sensor.sensorHumanId;

            return (
              <div
                key={sensor.sensorHumanId}
                draggable={!isEditing}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                  isEditing
                    ? 'border-blue-300 bg-blue-50/50 dark:border-blue-500/30 dark:bg-blue-500/10'
                    : 'border-gray-200 bg-white dark:border-white/[0.08] dark:bg-zinc-900'
                } ${draggedIndex === index ? 'opacity-50' : ''} ${
                  dragOverIndex === index && draggedIndex !== index
                    ? 'ring-2 ring-blue-500 ring-offset-1'
                    : ''
                } ${!isEditing ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                {!isEditing && (
                  <GripVertical className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-zinc-600" />
                )}

                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder={originalName}
                        className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 dark:border-white/[0.08] dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-white/[0.04]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-gray-900 dark:text-white">
                          {sensor.displayName || originalName}
                        </div>
                        {sensor.displayName && (
                          <div className="truncate text-xs text-gray-500 dark:text-zinc-500">
                            {originalName}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(index)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-zinc-300"
                        title="Edit display name"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                        title="Remove sensor"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add sensor dropdown (only shown if under max) */}
      {sensors.length < MAX_SENSORS && (
        <div className="relative">
          <button
            type="button"
            onClick={isOpen ? handleCloseDropdown : handleOpenDropdown}
            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
              error
                ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
            }`}
          >
            <span className="text-gray-500 dark:text-zinc-500">Add sensor...</span>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={handleCloseDropdown} />
              <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                {/* Search input */}
                <div className="border-b border-gray-200 p-2 dark:border-white/[0.08]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search sensors..."
                      className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-500"
                    />
                  </div>
                </div>

                {/* Sensor list */}
                <div className="max-h-48 overflow-auto py-1">
                  {isLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                      Loading sensors...
                    </div>
                  ) : availableSensors.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                      {allSensors.length === 0 ? 'No sensors available' : 'All sensors selected'}
                    </div>
                  ) : filteredSensors.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                      No sensors match "{searchQuery}"
                    </div>
                  ) : (
                    filteredSensors.map((sensor) => (
                      <button
                        key={sensor.id}
                        type="button"
                        onClick={() => handleAdd(sensor)}
                        className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                      >
                        <div>
                          <div className="text-gray-900 dark:text-white">
                            {sensor.name || sensor.humanId}
                          </div>
                          {sensor.name && sensor.humanId && (
                            <div className="text-xs text-gray-500 dark:text-zinc-500">
                              {sensor.humanId}
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* At max message */}
      {sensors.length >= MAX_SENSORS && (
        <p className="text-xs text-gray-500 dark:text-zinc-500">
          Maximum {MAX_SENSORS} sensors reached
        </p>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Query Value widget
 */
function QueryValueConfigForm({ config, onChange, errors }: ConfigFormProps<QueryValueConfig>) {
  const showLabelsOption = config.sensors.length > 1;

  return (
    <div className="space-y-4">
      <SensorReorderableMultiSelect
        sensors={config.sensors}
        onChange={(sensors) => onChange({ ...config, sensors })}
        error={errors?.['sensors']}
      />

      {/* Options - apply globally to all sensors */}
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.showUnit ?? true}
            onChange={(e) => onChange({ ...config, showUnit: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
          />
          <span className="text-sm text-gray-700 dark:text-zinc-300">Show unit</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.showLastUpdated ?? false}
            onChange={(e) => onChange({ ...config, showLastUpdated: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
          />
          <span className="text-sm text-gray-700 dark:text-zinc-300">Show last updated</span>
        </label>
        {showLabelsOption && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.showLabels ?? true}
              onChange={(e) => onChange({ ...config, showLabels: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-white/[0.12] dark:bg-zinc-900"
            />
            <span className="text-sm text-gray-700 dark:text-zinc-300">Show sensor labels</span>
          </label>
        )}
      </div>
    </div>
  );
}

const INTEGER_FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatValue(value?: number): string {
  if (value === undefined || value === null) return '—';
  if (Number.isInteger(value)) {
    return INTEGER_FORMATTER.format(value);
  }
  return ONE_DECIMAL_FORMATTER.format(value);
}

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

/**
 * Single sensor value display component
 */
function SensorValueDisplay({
  sensorId,
  displayName,
  sensor,
  isLoading,
  isError,
  showUnit,
  showLastUpdated,
  showLabel,
  totalSensors,
}: {
  sensorId: string;
  displayName?: string;
  sensor: SensorsSensor | undefined;
  isLoading: boolean;
  isError: boolean;
  showUnit: boolean;
  showLastUpdated: boolean;
  showLabel: boolean;
  totalSensors: number;
}) {
  const valueStr = formatValue(sensor?.value);

  const getFontSizeClass = () => {
    if (totalSensors === 1) {
      const valueLength = valueStr.length;
      if (valueLength <= 3) return 'text-5xl';
      if (valueLength <= 5) return 'text-4xl';
      if (valueLength <= 7) return 'text-3xl';
      return 'text-2xl';
    }
    if (totalSensors <= 3) return 'text-3xl';
    if (totalSensors <= 5) return 'text-2xl';
    return 'text-xl';
  };

  const getUnitSizeClass = () => {
    if (totalSensors === 1) {
      const valueLength = valueStr.length;
      if (valueLength <= 3) return 'text-xl';
      if (valueLength <= 5) return 'text-lg';
      return 'text-base';
    }
    if (totalSensors <= 3) return 'text-base';
    return 'text-sm';
  };

  if (isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        style={{ minWidth: MIN_SENSOR_WIDTH_PX }}
      >
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError || !sensor) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center px-1 text-center"
        style={{ minWidth: MIN_SENSOR_WIDTH_PX }}
      >
        <AlertTriangle className="mb-1 h-4 w-4 text-amber-500" />
        <p className="max-w-full truncate text-[10px] text-gray-500 dark:text-zinc-500">
          {displayName || sensorId}
        </p>
      </div>
    );
  }

  const labelText = displayName || sensor.name || sensorId;

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-2"
      style={{ minWidth: MIN_SENSOR_WIDTH_PX }}
    >
      {/* Sensor label for multi-sensor display */}
      {showLabel && totalSensors > 1 && (
        <p className="mb-1 max-w-full truncate text-[10px] text-gray-500 dark:text-zinc-500">
          {labelText}
        </p>
      )}

      <div className="flex items-baseline gap-1">
        <span
          className={`${getFontSizeClass()} font-semibold tabular-nums text-gray-900 dark:text-white`}
          style={sensor.unitColor ? { color: sensor.unitColor } : undefined}
        >
          {valueStr}
        </span>
        {showUnit && sensor.unitSymbol && (
          <span className={`${getUnitSizeClass()} text-gray-500 dark:text-zinc-400`}>
            {sensor.unitSymbol}
          </span>
        )}
      </div>

      {showLastUpdated && sensor.lastSeen && (
        <p className="mt-1 text-[10px] text-gray-400 dark:text-zinc-500">
          {formatRelativeTime(sensor.lastSeen)}
        </p>
      )}
    </div>
  );
}

/**
 * Renderer for Query Value widget
 */
const QueryValueRenderer = memo(function QueryValueRenderer({
  config,
}: RendererProps<QueryValueConfig>) {
  const queryClient = useQueryClient();

  const sensors = useMemo(() => normalizeConfig(config), [config]);
  const sensorIds = useMemo(() => sensors.map((s) => s.sensorHumanId), [sensors]);
  const showUnit = config.showUnit ?? true;
  const showLastUpdated = config.showLastUpdated ?? false;
  const showLabels = config.showLabels ?? true;

  // Parallel fetch all sensors
  const sensorQueries = useQueries({
    queries: sensorIds.map((sensorId) =>
      getGetSensorQueryOptions(sensorId, {
        query: {
          enabled: !!sensorId,
          refetchInterval: 30000,
        },
      })
    ),
  });

  // Subscribe to sensor stream for all sensors
  const handleSensorEvent = useCallback(() => {
    for (const sensorId of sensorIds) {
      queryClient.invalidateQueries({
        queryKey: getGetSensorQueryKey(sensorId),
      });
    }
  }, [queryClient, sensorIds]);

  useSensorStream({
    filter: sensorIds.join(','),
    enabled: sensorIds.length > 0,
    onEvent: handleSensorEvent,
  });

  const isLoading = sensorQueries.every((q) => q.isLoading);
  const allErrors = sensorQueries.every((q) => q.isError);

  if (sensors.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No sensor configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (allErrors) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load sensors</p>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
      <div
        className="flex h-full w-full"
        style={{ minWidth: `${sensors.length * MIN_SENSOR_WIDTH_PX}px` }}
      >
        {sensors.map((sensorEntry, index) => {
          const query = sensorQueries[index];
          const sensor = query?.data?.sensor;

          return (
            <SensorValueDisplay
              key={sensorEntry.sensorHumanId}
              sensorId={sensorEntry.sensorHumanId}
              displayName={sensorEntry.displayName}
              sensor={sensor}
              isLoading={query?.isLoading ?? false}
              isError={query?.isError ?? false}
              showUnit={showUnit}
              showLastUpdated={showLastUpdated}
              showLabel={showLabels}
              totalSensors={sensors.length}
            />
          );
        })}
      </div>
    </div>
  );
});

/**
 * Query Value widget definition
 */
export const queryValueWidget: WidgetDefinition<QueryValueConfig> = {
  type: 'query-value',
  displayName: 'Sensor Value',
  description: 'Display the current value of one or more sensors',
  icon: Activity,
  defaultSize: { w: 2, h: 2 },
  minSize: { w: 1, h: 1 },
  maxSize: { w: 12, h: 3 },
  configSchema: queryValueConfigSchema,
  defaultConfig: {
    sensors: [],
    showUnit: true,
    showLastUpdated: false,
    showLabels: true,
  },
  ConfigForm: QueryValueConfigForm,
  Renderer: QueryValueRenderer,
};
